
import { clamp01 } from "../clamp";
// The manifest `tools/build_sfx.sh` writes beside the clips it built -- the
// one record of what `client/public/sfx/` holds. Imported at build time so the
// creature families below and the loader's fetch list are read off it rather
// than kept in step by hand.
import manifest from "../../public/sfx/index.json";
// The exception table `tools/import_itemsound.py` derives from
// `metin3/data/item_proto.json`: which reachable vnums are the sub-types the
// original's `__LoadGameSound` (`playersettingmodule.py:321-333`) gives a
// different clip than their `BagSlot.kind`'s own default. See `equipSoundFor`.
import itemSoundTable from "./itemsound.json";

export type SfxName =
  | "swing"
  | "hit"
  | "hurt"
  // A creature's own death cry / pain vocal / attack vocal, keyed by its
  // species SET -- the model FOLDER the original loads its
  // `d:/ymir work/monster/<folder>/{dead,damage,attack}_N.wav` from. Every
  // folder the 2014 pack ships a set for (`CREATURE_SETS`, read off the
  // manifest `tools/build_sfx.sh` writes) is its own species; a name whose
  // folder ships no set resolves to the base race (`wolf`) instead -- never
  // silence, never always the dog, the original's own base-race behaviour.
  // A shipped set that has no take for one verb (a `fire_dragon` has no
  // damage wav and no `21.mss`) is silent for that verb, as the original is:
  // `RaceMotionData.cpp:459-469` derives the `.mss` path from the motion's
  // own and a motion with no script fires no sound. `growl_*`
  // is the attack vocal, distinct from `hit` (the weapon's clang, played for
  // any connecting blow) and `pain_*` (what a struck creature makes). See
  // `creatureDieSound` / `creaturePainSound` / `creatureAttackSound`.
  | `die_${string}`
  | `pain_${string}`
  | `growl_${string}`
  | "step_ground"
  | "step_dirt"
  | "levelup"
  | "money"
  | "click"
  | "pick"
  | "metinstone_insert"
  // Drinking a potion. One clip for either colour: the original registers a
  // single `USESOUND_POTION` (`playersettingmodule.py:326`) and
  // `CPythonItem::PlayUsePotionSound` (`PythonItem.cpp:187-190`) plays it for
  // SE_HPUP_RED and SE_SPUP_BLUE alike -- and only for your own character
  // (`PythonNetworkStreamPhaseGameItem.cpp:873-878`).
  | "eat_potion"
  // The warrior's own exertion grunt on a one-hand-sword combo step -- a
  // second event the same `.mss` that names the blade's `swing_N` also
  // carries, at its own offset. See `attacksound.ts`, the only caller: it
  // asks for a specific `variant` (1..4, one per combo step) rather than the
  // random pick every other family gets, because the original ties them
  // together deterministically too.
  | "grunt"
  // The unarmed punch's own per-race sound, APX-009 in
  // docs/audits/approximations.json closed: `general/attack.mss` exists once
  // per one of the original's eight races and each names its own clip(s) at
  // its own offset(s). `hand1`/`hand3` are the shared, weapon-agnostic fist
  // thuds three races each play; `sura`, `warrior_f` and the two `shaman_*`
  // are a single race's own grunt or vocal. See `attacksound.ts`'s
  // `PUNCH_CUES_BY_RACE`, the only caller -- it always names an exact
  // `variant` (there is only ever one take per clip) rather than a random
  // pick, the same as `grunt`.
  | "punch_hand1"
  | "punch_hand3"
  | "punch_sura"
  | "punch_warrior_f"
  | "punch_shaman_f"
  | "punch_shaman_m"
  // `__LoadGameSound` (`playersettingmodule.py:321-333`): one clip per item
  // kind, shared by the use (equip/unequip -- the original's `UseItem` is one
  // call for both directions) and drop sound tables alike, since every
  // `DROPSOUND_*` equals its `USESOUND_*` sibling except DEFAULT. See
  // `equipSoundFor`, the only thing that picks between these four plus `drop`.
  | "equip_weapon"
  | "equip_bow"
  | "equip_armor"
  | "equip_accessory"
  // `USESOUND_DEFAULT`/`DROPSOUND_DEFAULT` (`:321,329`): every kind with no
  // equip family of its own -- an `ITEM_USE` bundle, a quest item, a potion
  // or portal scroll dropped rather than used (their own `eat_potion`/none-
  // shipped families are use-only, matching the original's own table).
  | "drop"
  // `intrologin.py:419,466`'s `OnConnectFailure`/`OnLoginFailure`. The
  // original also reuses this clip for a refused sell/stall-slot click
  // (`uishop.py:342`, `uiprivateshopbuilder.py:260`) -- checked against this
  // build's own `shop.ts`/`pcshop.ts`, which send every buy/sell/list intent
  // unconditionally and let the server's `Notice` be the only refusal (see
  // those files' own docstrings), so neither gesture exists here to reuse it.
  | "loginfail"
  // `intrologin.py:434`'s `OnHandShake` -- the socket handshake succeeding.
  | "loginok"
  // `game.py:1938`'s `RefineSuceededMessage` (misspelling the original's own).
  | "make_soket"
  // `game.py:1942`'s `RefineFailedMessage`.
  | "jaeryun_fail"
  // `ui.py:687`'s `OnIMEUpdate`, fired on every text-buffer update -- one
  // clip per keystroke on this build's plain text entry. See `ui/chat.ts`.
  | "type"
  // The loops the map's ambience zones play: `waterfall.pra` and `warp.pra`
  // (`MapType.cpp:216-262`). See `audio/ambience.ts`, the only caller.
  | "ambience_waterfall"
  | "ambience_warp";

/** Every clip this build ships, by name: `tools/build_sfx.sh` lists the
 *  same names it wrote as `<name>.opus` and `<name>.m4a`. */
const SHIPPED_CLIPS: readonly string[] = Object.keys(manifest.clips);

const CREATURE_CLIP = /^(die|pain|growl)_(.+?)(?:_(\d+))?$/;

const CREATURE_SETS: ReadonlySet<string> = new Set<string>();
const CREATURE_VARIANTS: Record<string, readonly string[]> = {};
{
  const variants: Record<string, string[]> = {};
  for (const clip of SHIPPED_CLIPS) {
    const match = CREATURE_CLIP.exec(clip);
    if (match === null || match[2] === "player") continue;
    const [, verb, set] = match as unknown as [string, string, string];
    (CREATURE_SETS as Set<string>).add(set);
    (variants[`${verb}_${set}`] ??= []).push(clip);
  }
  Object.assign(CREATURE_VARIANTS, variants);
}

/** Clips per family. `swing` picks one of `swing_1..4` at random. */

const VARIANTS: Readonly<Record<string, readonly string[]>> = {
  swing: ["swing_1", "swing_2", "swing_3", "swing_4"],
  hit: ["hit_1", "hit_2", "hit_3"],
  hurt: ["hurt_1", "hurt_2", "hurt_3"],
  // The player's own death cry (`pc/warrior/voice/dead_1.wav`) -- a `die_*`
  // family that is not a monster species, so it keeps its own entry here and
  // is never subject to the base-race fallback the creature families are.
  die_player: ["die_player"],
  // Every creature family (`die_<set>` / `pain_<set>_N` / `growl_<set>`) for
  // the folders in `CREATURE_SETS`. The pain vocal a species makes when struck
  // is distinct from `hit` (the weapon's clang, played for any connecting
  // blow); some folders shipped two damage takes and some one.
  ...CREATURE_VARIANTS,
  step_ground: ["step_ground_1", "step_ground_2"],
  step_dirt: ["step_dirt_1", "step_dirt_2"],
  levelup: ["levelup"],
  // Gold changing hands -- a sale, a purchase, a safebox withdrawal, a drop.
  // The original plays the same clip for all four.
  money: ["money"],
  click: ["click"],
  ambience_waterfall: ["ambience_waterfall"],
  ambience_warp: ["ambience_warp"],
  // Picking an item up into the cursor, in the original's own inventory, shop
  // and safebox panels. Not a target-selection sound -- the original has none
  // -- which is what this family used to be called before it was renamed to
  // what the clip underneath it actually is.
  pick: ["pick"],
  // The Metin-stone socket confirmation (`uiattachmetin.py:132`'s `Accept()`),
  // played the instant the request is sent -- win or lose the roll, the
  // original does not wait for a server reply either.
  metinstone_insert: ["metinstone_insert"],
  // One take, and one for both potion colours -- see the type above.
  eat_potion: ["eat_potion"],
  // Never picked at random -- `attacksound.ts` always names an exact
  // `variant`, 1..4, matching the combo step the grunt fired for.
  grunt: ["grunt_1", "grunt_2", "grunt_3", "grunt_4"],
  // Never picked at random -- like `grunt`, `attacksound.ts` always names the
  // exact clip its per-race table resolved. Single-take families: none of
  // these six ever shipped a second variant in the original.
  punch_hand1: ["punch_hand1"],
  punch_hand3: ["punch_hand3"],
  punch_sura: ["punch_sura"],
  punch_warrior_f: ["punch_warrior_f"],
  punch_shaman_f: ["punch_shaman_f"],
  punch_shaman_m: ["punch_shaman_m"],
  equip_weapon: ["equip_weapon"],
  equip_bow: ["equip_bow"],
  equip_armor: ["equip_armor"],
  equip_accessory: ["equip_accessory"],
  drop: ["drop"],
  loginfail: ["loginfail"],
  loginok: ["loginok"],
  make_soket: ["make_soket"],
  jaeryun_fail: ["jaeryun_fail"],
  type: ["type"],
};

/** Simultaneous voices. Past this the oldest is stopped rather than the newest
 *  dropped: the sound a player is looking at is the one that just started. */
const MAX_VOICES = 12;

const GAINS: Readonly<Record<string, number>> = {
  swing: 0.35,
  hit: 0.55,
  hurt: 0.7,
  die_player: 0.7,
  step_ground: 0.3,
  step_dirt: 0.3,
  levelup: 0.85,
  money: 0.5,
  click: 0.4,
  // `PlayAmbienceSound3D` starts at the sound volume and `SetSoundVolume3D`
  // then sets the zone's own distance volume (`SoundManager.cpp:356-377,432-440`).
  ambience_waterfall: 1,
  ambience_warp: 1,
  pick: 0.45,
  metinstone_insert: 0.5,
  // A confirmation, at a confirmation's loudness: the same level as the two
  // other UI acknowledgements above it, `money` and `metinstone_insert`.
  eat_potion: 0.5,
  // Quieter than `swing`: two events fire almost together on every step and
  // the blade, not the breath, is meant to read as the primary sound.
  grunt: 0.3,
  punch_hand1: 0.35,
  punch_hand3: 0.35,
  punch_sura: 0.35,
  punch_warrior_f: 0.3,
  punch_shaman_f: 0.3,
  punch_shaman_m: 0.3,
  // Confirmations, at `money`/`metinstone_insert`'s own loudness.
  equip_weapon: 0.5,
  equip_bow: 0.5,
  equip_armor: 0.5,
  equip_accessory: 0.5,
  drop: 0.5,
  loginfail: 0.5,
  loginok: 0.5,
  make_soket: 0.5,
  jaeryun_fail: 0.5,
  // Quieter: this one fires on every keystroke rather than once per action.
  type: 0.3,
};

/** The loudness for a family: the explicit `GAINS` entry, else the creature
 *  prefix default (a death cry is a touch louder than a pain or attack vocal),
 *  else a neutral middle. */
function gainFor(name: SfxName): number {
  const explicit = GAINS[name];
  if (explicit !== undefined) return explicit;
  if (name.startsWith("die_")) return 0.6;
  if (name.startsWith("pain_") || name.startsWith("growl_")) return 0.5;
  return 0.5;
}

/** A monster sound SET -- a folder in `CREATURE_SETS`, e.g. `wolf`, `bear`,
 *  `spider_queen`, `fire_dragon`. Validated at runtime against `SPECIES`. */
type Species = string;

/** The sound sets this build ships, so a folder-derived `sound` value from
 *  `creatures.json` can be validated back to a known set rather than trusted
 *  blind -- an unknown value ("unicorn", a typo, "") is dropped. */
const SPECIES: ReadonlySet<string> = CREATURE_SETS;

const SPECIES_BY_CREATURE: Readonly<Record<string, Species>> = {
  "Wild Dog": "dog",
  Wolf: "wolf",
  "Alpha Wolf": "wolf",
  "Blue Wolf": "wolf",
  "Alpha Blue Wolf": "wolf",
  "Grey Wolf": "wolf",
  "Alpha Grey Wolf": "wolf",
  "Cung-Mok": "wolf",
  "Mu-Rang": "wolf",
  "Wild Boar": "boar",
  "Red Wild Boar": "boar",
  "Jug-Hyul": "boar",
  Bear: "bear",
  "Young-Ji": "bear",
  "Li-An": "tiger",
  Lykos: "lykos",
};

/** The name->species table `main.ts` registers from `creatures.json` at boot,
 *  covering every monster of every shipped map. Overlays -- never replaces --
 *  the built-in `SPECIES_BY_CREATURE`, so a name the data omits still resolves
 *  through the boot table below it. Empty until registered. */
let registeredSpecies: Readonly<Record<string, Species>> = {};

export function registerCreatureSounds(map: Readonly<Record<string, string>>): void {
  const valid: Record<string, Species> = {};
  for (const [name, species] of Object.entries(map)) {
    if (SPECIES.has(species)) valid[name] = species;
  }
  registeredSpecies = valid;
}

/** The species a creature's display name resolves to: the registered data
 *  first, then the boot-time built-in, then the base race -- the wolf, this
 *  map's commonest species and the original's own base-race fallback, never
 *  silence and never always the dog. */
function speciesFor(name: string): Species {
  return registeredSpecies[name] ?? SPECIES_BY_CREATURE[name] ?? "wolf";
}

export function creatureDieSound(name: string): SfxName {
  return `die_${speciesFor(name)}`;
}

/** The pain vocal a creature makes when struck, by its display name. Same
 *  resolution and fallback as `creatureDieSound`. */
export function creaturePainSound(name: string): SfxName {
  return `pain_${speciesFor(name)}`;
}

export function creatureAttackSound(name: string): SfxName {
  return `growl_${speciesFor(name)}`;
}

const BOW_VNUMS: ReadonlySet<number> = new Set(itemSoundTable.bow);
const ACCESSORY_VNUMS: ReadonlySet<number> = new Set(itemSoundTable.accessory);

export function equipSoundFor(kind: number, vnum: number): SfxName {
  if (kind === 1) return BOW_VNUMS.has(vnum) ? "equip_bow" : "equip_weapon";
  if (kind === 2) return ACCESSORY_VNUMS.has(vnum) ? "equip_accessory" : "equip_armor";
  return "drop";
}

export interface SfxOptions {
  readonly base?: string;
  readonly volume?: number;
  /** Injected in tests. Defaults to the browser's constructor. */
  readonly createContext?: () => AudioContext;
  readonly fetchClip?: (url: string) => Promise<ArrayBuffer>;
}

export interface PlayOptions {
  /** -1 hard left to 1 hard right. Clamped. Ignored where panning is absent. */
  readonly pan?: number;
  /** 0..1, multiplied into the family gain. Distance attenuation lives here. */
  readonly volume?: number;
  readonly delayMs?: number;
  readonly variant?: number;
}

export interface SfxPlayer {
  play(name: SfxName, options?: PlayOptions): void;
}

interface Voice {
  source: AudioBufferSourceNode;
  startedAt: number;
}

export class Sfx {
  private readonly base: string;
  private readonly buffers = new Map<string, AudioBuffer>();
  private readonly voices: Voice[] = [];
  private readonly create: () => AudioContext;
  private readonly fetchClip: (url: string) => Promise<ArrayBuffer>;

  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private volumeValue: number;
  private mutedValue = false;
  private loading: Promise<void> | null = null;
  private retryGesture: (() => void) | null = null;

  /** True once the buffers are decoded and the context is running. */
  ready = false;

  constructor(options: SfxOptions = {}) {
    this.base = (options.base ?? "/sfx").replace(/\/$/, "");
    this.volumeValue = clamp01(options.volume ?? 0.6);
    this.create =
      options.createContext ??
      (() => new (window.AudioContext ?? (window as never as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)());
    this.fetchClip =
      options.fetchClip ??
      (async (url) => {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`${url}: HTTP ${response.status}`);
        return response.arrayBuffer();
      });
  }

  unlock(): Promise<void> {
    if (this.loading !== null) return this.loading;
    if (this.ready) {
      const context = this.context;
      // Safari may suspend an already-unlocked context after a tab switch or
      // audio interruption. Decoding is still valid; only resume it from the
      // next real gesture rather than silently treating `ready` as permanent.
      if (context === null || context.state !== "suspended") return Promise.resolve();
      return this.trackUnlock(context.resume().catch((error: unknown) => {
        console.warn(`sound effects unavailable (${String(error)})`);
      }));
    }
    const pending = this.start().catch((error: unknown) => {
      // Silence, not a broken game. Logged once, at the only point where the
      // difference between "no sound card" and "a typo in a filename" is
      // visible to whoever is debugging it.
      console.warn(`sound effects unavailable (${String(error)})`);
    });
    return this.trackUnlock(pending);
  }

  /** Keep one resume/decode attempt in flight and arrange a later gesture when
   * Safari refused it. The same bookkeeping serves initial unlock and a
   * context that was suspended after it had already become ready. */
  private trackUnlock(pending: Promise<void>): Promise<void> {
    this.loading = pending;
    // A rejected resume is recoverable on a later gesture. Keep the in-flight
    // guard until this attempt settles, then permit one retry and have it
    // happen from the same pointer gesture Safari requires for resume().
    void pending.then(() => {
      if (this.loading !== pending) return;
      this.loading = null;
      if (this.ready && this.context?.state === "running") this.removeRetryGesture();
      else this.installRetryGesture();
    });
    return pending;
  }

  private async start(): Promise<void> {
    const context = this.context ?? this.create();
    this.context = context;

    if (this.master === null) {
      const master = context.createGain();
      master.connect(context.destination);
      this.master = master;
    }
    this.master.gain.value = this.mutedValue ? 0 : this.volumeValue;

    // Suspended is the normal state for a context built before a gesture, and
    // `resume` is what a gesture is for. It rejects if there has not been one.
    if (context.state === "suspended") await context.resume();

    // Exactly the clips the manifest says were built: a creature family is
    // derived from it, so no fetch is spent on a take a folder never shipped.
    const names = [...new Set(Object.values(VARIANTS).flat())];
    const extension = this.extension(context);
    await Promise.all(
      names.map(async (name) => {
        try {
          const bytes = await this.fetchClip(`${this.base}/${name}${extension}`);
          this.buffers.set(name, await context.decodeAudioData(bytes));
        } catch {
          // One clip missing is one sound missing. The rest still play, and
          // `play` treats an absent buffer as nothing to do.
        }
      }),
    );

    this.ready = this.buffers.size > 0;
  }

  private installRetryGesture(): void {
    if (this.retryGesture !== null || typeof document === "undefined") return;
    this.retryGesture = () => {
      void this.unlock();
    };
    document.addEventListener("pointerdown", this.retryGesture, { capture: true });
  }

  private removeRetryGesture(): void {
    if (this.retryGesture === null || typeof document === "undefined") return;
    document.removeEventListener("pointerdown", this.retryGesture, { capture: true });
    this.retryGesture = null;
  }

  /** Which encoding this browser can decode. Opus everywhere but old Safari. */
  private extension(context: AudioContext): string {
    void context;
    try {
      const probe = document.createElement("audio");
      if (probe.canPlayType('audio/ogg; codecs="opus"') !== "") return ".opus";
    } catch {
      // No DOM to ask. Opus is the safer guess in every environment that has
      // WebAudio at all and no `<audio>` element to interrogate.
    }
    return ".m4a";
  }

  play(name: SfxName, options: PlayOptions = {}): void {
    const context = this.context;
    const master = this.master;
    if (context === null || master === null) return;
    if (context.state !== "running") {
      // `ready` only means the buffers decoded. A backgrounded Safari tab can
      // suspend the context later; the next tap must get a chance to resume it.
      if (context.state === "suspended") this.installRetryGesture();
      return;
    }

    const buffer = this.pick(name, options.variant);
    if (buffer === null) return;

    try {
      this.reap(context.currentTime);

      const source = context.createBufferSource();
      source.buffer = buffer;

      const gain = context.createGain();
      gain.gain.value = gainFor(name) * clamp01(options.volume ?? 1);

      // `createStereoPanner` is absent on old Safari; the sound is still worth
      // playing without a position.
      let tail: AudioNode = gain;
      if (options.pan !== undefined && typeof context.createStereoPanner === "function") {
        const panner = context.createStereoPanner();
        panner.pan.value = Math.max(-1, Math.min(1, options.pan));
        gain.connect(panner);
        tail = panner;
      }
      tail.connect(master);
      source.connect(gain);

      // Scheduled against the audio clock, not `setTimeout`: a swing 132 ms
      // into a clip has to land within a few milliseconds of that or the
      // sync it exists for is the thing that is lost.
      const startAt = context.currentTime + Math.max(0, (options.delayMs ?? 0) / 1000);
      const voice: Voice = { source, startedAt: startAt };
      source.onended = () => {
        const at = this.voices.indexOf(voice);
        if (at >= 0) this.voices.splice(at, 1);
      };
      this.voices.push(voice);
      source.start(startAt);
    } catch {
      // A context that died under us -- a tab suspended, a device unplugged.
      // Not worth a console line per swing.
    }
  }

  /** A looping voice for one ambience zone, or null before a gesture or for a
   *  clip that did not decode. It is not one of the capped voices: a zone that
   *  is still in range keeps playing however many swings are heard. */
  loop(name: SfxName, volume: number): { setVolume(volume: number): void; stop(): void } | null {
    const context = this.context;
    const master = this.master;
    if (context === null || master === null || context.state !== "running") return null;
    const buffer = this.pick(name, 1);
    if (buffer === null) return null;
    try {
      const source = context.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      const gain = context.createGain();
      const family = gainFor(name);
      gain.gain.value = family * clamp01(volume);
      source.connect(gain);
      gain.connect(master);
      source.start();
      return {
        setVolume: (next) => {
          gain.gain.value = family * clamp01(next);
        },
        stop: () => {
          try {
            source.stop();
          } catch {
            // Already stopped.
          }
        },
      };
    } catch {
      return null;
    }
  }

  /** Stop the oldest voices until there is room for one more. */
  private reap(now: number): void {
    void now;
    while (this.voices.length >= MAX_VOICES) {
      const oldest = this.voices.shift();
      try {
        oldest?.source.stop();
      } catch {
        // Already finished. `stop` on a stopped source throws in some engines.
      }
    }
  }

  private pick(name: SfxName, variant?: number): AudioBuffer | null {
    const names = VARIANTS[name];
    if (names === undefined || names.length === 0) return null;
    if (variant !== undefined) {
      // Clamped, not rejected: an out-of-range step (a fifth combo swing this
      // pack does not model, say) still plays the nearest real clip rather
      // than nothing.
      const index = Math.min(names.length, Math.max(1, Math.round(variant))) - 1;
      return this.buffers.get(names[index]!) ?? null;
    }
    // Random among the clips that actually decoded, so an unconverted variant
    // (a `pain_<set>_2` a folder never shipped) reads as its sibling rather
    // than as an occasional silence.
    const loaded = names.filter((clip) => this.buffers.has(clip));
    if (loaded.length === 0) return null;
    const chosen = loaded[Math.floor(Math.random() * loaded.length)]!;
    return this.buffers.get(chosen) ?? null;
  }

  get volume(): number {
    return this.volumeValue;
  }

  set volume(value: number) {
    this.volumeValue = clamp01(value);
    if (this.master !== null && !this.mutedValue) {
      this.master.gain.value = this.volumeValue;
    }
  }

  get muted(): boolean {
    return this.mutedValue;
  }

  set muted(value: boolean) {
    this.mutedValue = value;
    if (this.master !== null) this.master.gain.value = value ? 0 : this.volumeValue;
  }

  /** Live voices. For tests and for a frame-budget readout. */
  get voiceCount(): number {
    return this.voices.length;
  }
}

export function spatial(
  dxMm: number,
  dyMm: number,
  rangeMm: number,
): PlayOptions {
  const distance = Math.hypot(dxMm, dyMm);
  const volume = distance >= rangeMm ? 0 : 1 - distance / rangeMm;
  // Panned by the horizontal offset alone, and only to 0.8 -- a hard-panned
  // sound in headphones reads as being inside one ear rather than to one side.
  const pan = rangeMm <= 0 ? 0 : Math.max(-0.8, Math.min(0.8, (dxMm / rangeMm) * 1.6));
  return { pan, volume };
}
